/**
 * Share Modal Module
 * UI for sharing documents with other users (document-level sharing)
 */

import { createModal, showModal, closeModal } from "./modals.js";
import { escapeHtml, escapeAttr } from "../../../engine/core/validation.js";
import { Keypair, KeypairManager } from "../../../engine/crypto/keypair.js";
import * as MapKeyTrust from '../../../engine/crypto/map-key-trust.js';
import { VaultStorage, isOnlineSlotEnabled } from "../../../engine/crypto/vault-storage.js";
import { mapStorageOf, moveMapOnline } from "./map-storage.js";
import { rotateShareDEK } from "../../../engine/sync/share-rotation.js";
import { beginOperation } from "./operation-progress.js";
import { engine } from "../../../engine/index.js";
import { on, Events } from "../../../engine/core/events.js";
import { showToast } from "./toast.js";
import { getConnectionState as getVaultConnectionState } from "../../../engine/sync/user-crdt-bridge.js";
import { t } from "../../../shared/i18n/index.js";

// ============================================
// State
// ============================================

let keypairManager = null;

// ============================================
// Role labels
// ============================================

const ROLES = ["owner", "admin", "editor", "viewer"];

/**
 * A role's label ("Editor") in the app's language. Only the text shown is
 * translated: the role values sent to the server stay "viewer", "editor"…
 */
export function roleLabel(role) {
  if (ROLES.includes(role)) return t(`share.role.${role}`);
  const text = String(role || "");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A role as written inside a sentence ("editor"). */
export function roleInText(role) {
  return ROLES.includes(role) ? t(`share.roleInText.${role}`) : String(role || "");
}

/** The `<option>`s of a role select (values stay the server's role names). */
function roleOptions(roles, selected) {
  return roles
    .map((role) => `<option value="${role}"${role === selected ? " selected" : ""}>${escapeHtml(roleLabel(role))}</option>`)
    .join("");
}

/** The legend under the role select of the share form. */
function roleLegendHtml() {
  return ["viewer", "editor", "admin"]
    .map((role) => `<div class="legend-item"><strong>${escapeHtml(t("share.legend.term", { role: roleLabel(role) }))}</strong> ${escapeHtml(t(`share.legend.${role}`))}</div>`)
    .join("");
}

/** The role shown for a member who can't change it. */
function roleLabelHtml(role) {
  return `<span class="permission-role-label">${escapeHtml(roleLabel(role))}</span>`;
}

/** "3 users" */
function memberCountText(count) {
  return t("share.members.count", { count });
}

/**
 * Initialize the share module
 * @param {Object} deps - Dependencies
 */
export async function initShareModule(deps = {}) {
  if (keypairManager) {
    keypairManager.destroy();
  }
  keypairManager = new KeypairManager(VaultStorage);

  const manager = keypairManager;
  try {
    await manager.initialize();
    console.log("[Share] Keypair initialized");
    afterKeypairReady(manager);
  } catch (error) {
    console.error("[Share] Failed to initialize keypair:", error);
    if (error?.code === "identity_unavailable") retryWhenVaultUpdates(manager);
  }
}

function afterKeypairReady(manager) {
  if (manager !== keypairManager) return; // superseded (logout / re-login)
  // Dynamic imports: both modules import this one.
  if (manager.identityMismatch) {
    confirmMismatchOnceVaultSynced(manager);
    return;
  }
  // Background: restore accepted maps an older client failed to add to the
  // vault (see adoptStrandedMemberships), and pick up maps whose owner
  // restored our access after an identity repair.
  import("./notifications.js")
    .then((m) => m.adoptStrandedMemberships())
    .then(() => MapKeyTrust.refreshRestoredShares())
    .catch((err) => console.warn("[Share] Shared map access check failed:", err));
}

// Sign-in continues on this device's local copy of the vault when the sync is
// slow, and a device that hasn't synced for a while holds an older key: the
// mismatch it sees is not real. Wait for the vault to be in sync with the
// server, re-read the key, and only then prompt — so no dialog opens for a
// mismatch that isn't real. While one stands, vault changes re-check it so
// sharing recovers if it's fixed from another device (an open dialog stays
// until the user acts on it).
const VAULT_SYNC_WAIT_MS = 120000;
async function confirmMismatchOnceVaultSynced(manager) {
  const deadline = Date.now() + VAULT_SYNC_WAIT_MS;
  while (manager === keypairManager && getVaultConnectionState() !== "synced" && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }
  if (manager !== keypairManager) return;
  if (getVaultConnectionState() !== "synced") {
    console.warn("[Share] Vault never synced; not prompting for identity repair on a local copy");
    return;
  }
  try {
    await manager.initialize();
  } catch (error) {
    console.error("[Share] Identity re-check failed:", error);
    return;
  }
  if (!manager.identityMismatch) {
    console.log("[Share] Identity matches once the vault synced — no repair needed");
    afterKeypairReady(manager);
    return;
  }
  import("./identity-repair.js")
    .then((m) => {
      m.maybePromptIdentityRepair();
      recheckIdentityOnVaultUpdates(manager);
    })
    .catch((err) => console.warn("[Share] Identity repair prompt failed:", err));
}

function recheckIdentityOnVaultUpdates(manager) {
  let busy = false;
  const off = on(Events.VAULT_INDEX_UPDATED, async () => {
    if (manager !== keypairManager || !manager.identityMismatch) { off?.(); return; }
    if (busy) return;
    busy = true;
    try {
      await manager.initialize();
      if (!manager.identityMismatch) {
        off?.();
        afterKeypairReady(manager);
      }
    } catch (error) {
      console.warn("[Share] Identity re-check failed:", error);
    } finally {
      busy = false;
    }
  });
}

// The account's key is published but this device's vault hasn't delivered it
// yet: try again when the vault changes instead of generating a new identity.
function retryWhenVaultUpdates(manager) {
  let busy = false;
  const off = on(Events.VAULT_INDEX_UPDATED, async () => {
    if (manager !== keypairManager) { off?.(); return; }
    if (busy) return;
    busy = true;
    try {
      await manager.initialize();
      off?.();
      console.log("[Share] Keypair initialized after vault sync");
      afterKeypairReady(manager);
    } catch (error) {
      if (error?.code !== "identity_unavailable") {
        off?.();
        console.error("[Share] Failed to initialize keypair:", error);
      }
    } finally {
      busy = false;
    }
  });
}

// ============================================
// Share Status Modal (entry point)
// ============================================

/**
 * Show the share modal for a document.
 * If the document is already shared, shows the manage modal.
 * If not shared, shows the create share modal.
 * @param {Object|null} node - Unused (kept for backward compat), pass null
 * @param {string} docId - The document ID to share
 * @returns {Promise<Object|null>} Action result or null if cancelled
 */
export async function showShareStatusModal(node, docId) {
  // In the unified model, docId is always the share identifier.
  // Always open the manage modal (which shows both current permissions and invite form).
  return showManageShareModal(docId, docId);
}

// ============================================
// Create Share Modal
// ============================================

/**
 * Show the modal to create a new document share
 * @param {string} docId - The document ID to share
 * @returns {Promise<Object|null>} Share result or null if cancelled
 */
export async function showCreateShareModal(docId) {
  const vaultIndex = VaultStorage.getIndex();
  const docEntry = vaultIndex?.docs?.find((d) => d.docId === docId);
  const docTitle = docEntry?.title || t("common.untitled");

  return new Promise((resolve) => {
    const modalId = "share-modal";

    const modal = createModal({
      id: modalId,
      title: t("share.create.title"),
      className: "modal-share",
      content: `
        <div class="share-modal-content">
          <div class="share-node-info">
            <div class="share-node-icon" style="background: #06b6d4"></div>
            <div class="share-node-details">
              <span class="share-node-name">${escapeHtml(docTitle)}</span>
              <span class="share-node-meta">${escapeHtml(t("share.create.entireDocument"))}</span>
            </div>
          </div>

          <div class="share-form">
            <div class="form-group">
              <label for="share-username">${escapeHtml(t("share.invite.heading"))}</label>
              <div class="input-with-icon">
                <input
                  type="text"
                  id="share-username"
                  class="form-input"
                  placeholder="${escapeAttr(t("share.invite.username"))}"
                  autocomplete="off"
                />
                <span class="input-icon" id="share-username-status"></span>
              </div>
              <span class="form-hint" id="share-username-hint"></span>
            </div>

            <div class="form-group">
              <label for="share-role">${escapeHtml(t("share.invite.permission"))}</label>
              <select id="share-role" class="form-select">
                ${roleOptions(["viewer", "editor", "admin"], "editor")}
              </select>
            </div>

            <div class="permission-legend">
              ${roleLegendHtml()}
            </div>
          </div>

          <div class="share-error" id="share-error"></div>
        </div>
      `,
      buttons: [
        { text: t("common.cancel"), action: "cancel", className: "modal-btn-secondary" },
        { text: t("common.share"), action: "share", className: "modal-btn-primary" },
      ],
    });

    let resolved = false;

    const cleanup = () => {
      closeModal(modalId);
      modal.remove();
    };

    const handleCancel = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(null);
    };

    const handleShare = async () => {
      if (resolved) return;

      const username = modal.querySelector("#share-username").value.trim();
      const role = modal.querySelector("#share-role").value;
      const errorEl = modal.querySelector("#share-error");
      const shareBtn = modal.querySelector('[data-action="share"]');

      if (!username) {
        showError(errorEl, t("share.error.noUsername"));
        return;
      }
      const currentUser = engine.account.user();
      if (
        currentUser &&
        username.toLowerCase() === currentUser.username.toLowerCase()
      ) {
        showError(errorEl, t("share.error.shareSelf"));
        return;
      }

      shareBtn.disabled = true;
      shareBtn.textContent = t("share.create.sharing");
      errorEl.style.display = "none";

      try {
        const result = await createShare({ docId, title: docTitle, username, role });
        // docId IS the share room — owner is already connected.
        resolved = true;
        cleanup();
        resolve(result);
      } catch (error) {
        console.error("[Share] Error creating share:", error);
        showError(errorEl, error.message || t("share.error.createFailed"));
        shareBtn.disabled = false;
        shareBtn.textContent = t("common.share");
      }
    };

    // Event handlers
    modal
      .querySelector('[data-action="cancel"]')
      .addEventListener("click", handleCancel);
    modal
      .querySelector('[data-action="share"]')
      .addEventListener("click", handleShare);
    modal
      .querySelector(".modal-close")
      ?.addEventListener("click", handleCancel);
    modal
      .querySelector(".modal-backdrop")
      ?.addEventListener("click", handleCancel);

    // Enter key to submit
    modal.querySelector("#share-username").addEventListener("keydown", (e) => {
      if (e.key === "Enter") handleShare();
    });

    // Username validation feedback
    let usernameTimeout = null;
    modal.querySelector("#share-username").addEventListener("input", (e) => {
      const hint = modal.querySelector("#share-username-hint");
      const username = e.target.value.trim();

      if (usernameTimeout) clearTimeout(usernameTimeout);
      hint.textContent = "";
      hint.className = "form-hint";

      if (username.length >= 2) {
        usernameTimeout = setTimeout(async () => {
          try {
            await keypairManager.getUserPublicKey(username);
            hint.textContent = t("share.invite.userFound");
            hint.className = "form-hint hint-success";
          } catch (error) {
            hint.textContent = t("share.invite.userNotFound");
            hint.className = "form-hint hint-error";
          }
        }, 500);
      }
    });

    document.body.appendChild(modal);
    showModal(modalId);
    modal.querySelector("#share-username").focus();
  });
}

// ============================================
// Share Management Modal
// ============================================

/**
 * Show modal to manage existing document share
 * @param {string} shareId - The share UUID
 * @param {string} docId - The document ID
 * @returns {Promise<Object|null>} Action result or null if cancelled
 */
export function showManageShareModal(shareId, docId = null) {
  return new Promise((resolve) => {
    (async () => { try {
    const modalId = "manage-share-modal";

    let share;
    try {
      share = await fetchShareDetails(shareId);
    } catch (error) {
      console.error("[Share] Error loading share:", error);
      resolve(null);
      return;
    }

    // Server returns empty title (E2EE) — use local vault title instead
    const vaultIndex = VaultStorage.getIndex();
    const localDoc = vaultIndex?.docs?.find((d) => d.docId === shareId);
    if (localDoc?.title) share.title = localDoc.title;
    if (!share.title) share.title = t("common.untitled");

    const isOwner = share.role === "owner";
    const isAdmin = share.role === "admin" || isOwner;
    const isEditor = share.role === "editor";
    const canViewPermissions = true; // Every member can inspect the shared document roster.

    // Build permissions list HTML (exclude owner — shown separately in header)
    const nonOwnerPerms = (share.permissions || []).filter(p => p.role !== "owner");
    const pinWarnings = pinWarningsFor(shareId);
    let permissionsHtml = "";
    if (nonOwnerPerms.length > 0) {
      const removeBtn = `
        <button class="btn-remove-user" title="${escapeAttr(t("share.member.remove"))}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      `;
      permissionsHtml = nonOwnerPerms
        .map((p) => {
          let actionsHtml = "";

          if (isAdmin) {
            // Removal = revoke + DEK rotation, which the backend restricts to
            // the owner (rotation.rs rejects revoke_user_ids from non-owners).
            // Rendering the button for admins/editors guaranteed a failure alert.
            actionsHtml = `
              <select class="permission-role" data-original-role="${escapeAttr(p.role)}">
                ${roleOptions(["viewer", "editor", "admin"], p.role)}
              </select>
              ${isOwner ? removeBtn : ""}
            `;
          } else if (isEditor) {
            if (p.role === "viewer" || p.role === "editor") {
              actionsHtml = `
                <select class="permission-role" data-original-role="${escapeAttr(p.role)}">
                  ${roleOptions(["viewer", "editor"], p.role)}
                </select>
              `;
            } else {
              actionsHtml = roleLabelHtml(p.role);
            }
          } else {
            actionsHtml = roleLabelHtml(p.role);
          }

          return `
          <div class="permission-item" data-user-id="${escapeAttr(p.user_id)}" data-role="${escapeAttr(p.role)}">
            <div class="permission-user">
              <span class="permission-avatar">${escapeHtml((p.username || "?")[0].toUpperCase())}</span>
              <span class="permission-username">${escapeHtml(p.username || t("share.member.unknown"))}</span>
            </div>
            <div class="permission-actions">
              ${keyRestoreHtml(p.key_restore_required, p.user_id, p.username, isOwner)}
              ${pinWarningHtml(pinWarnings.get(p.user_id))}
              ${actionsHtml}
            </div>
          </div>
        `;
        })
        .join("");
    } else {
      permissionsHtml = `
        <div class="empty-permissions">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <p>${escapeHtml(t("share.members.empty"))}</p>
          <span>${escapeHtml(t("share.members.emptyHint"))}</span>
        </div>
      `;
    }

    const modal = createModal({
      id: modalId,
      title: t("share.manage.title"),
      className: "modal-manage-share",
      content: `
        <div class="manage-share-content">
          <div class="share-status-header">
            <div class="share-status-icon ${isOwner ? "owner" : ""}">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <div class="share-status-details">
              <h3 class="share-title">${escapeHtml(share.title)}</h3>
              <div class="share-meta">
                <span class="share-role-badge ${escapeAttr(share.role)}">${escapeHtml(roleInText(share.role))}</span>
                ${share.owner_username && !isOwner ? `<span class="share-owner">${escapeHtml(t("share.manage.byOwner", { name: share.owner_username }))}</span>` : ""}
              </div>
            </div>
          </div>

          <div data-ref="authority-slot">${authorityNoticeHtml(shareId, isOwner)}</div>

          ${
            isAdmin
              ? `
          <div class="invite-section">
            <div class="section-header">
              <h4>${escapeHtml(t("share.invite.heading"))}</h4>
            </div>
            <div class="invite-form">
              <div class="invite-input-group">
                <input type="text" id="invite-username" class="form-input" placeholder="${escapeAttr(t("share.invite.username"))}" />
                <select id="invite-role" class="form-select">
                  ${roleOptions(["viewer", "editor", "admin"], "editor")}
                </select>
                <button id="btn-invite" class="btn-invite">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <line x1="20" y1="8" x2="20" y2="14"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                  ${escapeHtml(t("common.add"))}
                </button>
              </div>
              <div id="invite-error" class="share-error"></div>
            </div>
          </div>
          `
              : ""
          }

          ${
            canViewPermissions
              ? `
          <div class="permissions-section">
            <div class="section-header">
              <h4>${escapeHtml(t("share.members.heading"))}</h4>
              <span class="permission-count">${escapeHtml(memberCountText(nonOwnerPerms.length))}</span>
            </div>
            <div class="permissions-list">
              ${permissionsHtml}
            </div>
          </div>
          `
              : ""
          }

          ${
            isAdmin && share.pendingInvitations?.length > 0
              ? `
          <div class="pending-invitations-section">
            <div class="section-header">
              <h4>${escapeHtml(t("share.pending.heading"))}</h4>
              <span class="permission-count">${escapeHtml(t("share.pending.count", { count: share.pendingInvitations.length }))}</span>
            </div>
            <div class="permissions-list">
              ${share.pendingInvitations
                .map(
                  (inv) => `
                <div class="permission-item pending-invitation" data-invitation-id="${escapeAttr(inv.id)}">
                  <div class="permission-user">
                    <span class="permission-avatar pending">${escapeHtml((inv.invitee_username || "?")[0].toUpperCase())}</span>
                    <span class="permission-username">${escapeHtml(inv.invitee_username || t("share.member.unknown"))}</span>
                  </div>
                  <div class="permission-actions">
                    ${keyRestoreHtml(inv.key_restore_required, inv.invitee_id, inv.invitee_username, isOwner)}
                    ${pinWarningHtml(pinWarnings.get(inv.invitee_id))}
                    <span class="permission-role-label pending">${escapeHtml(t("share.pending.role", { role: roleInText(inv.role) }))}</span>
                    <button class="btn-cancel-invite" title="${escapeAttr(t("share.pending.cancel"))}">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                </div>
              `,
                )
                .join("")}
            </div>
          </div>
          `
              : ""
          }

          ${
            isOwner && nonOwnerPerms.length > 0
              ? `
          <div class="danger-section">
            <div class="section-header">
              <h4>${escapeHtml(t("share.danger.heading"))}</h4>
            </div>
            <div class="danger-content">
              <div class="danger-info">
                <strong>${escapeHtml(t("share.danger.stop"))}</strong>
                <p>${escapeHtml(t("share.danger.stopHint"))}</p>
              </div>
              <button id="btn-unshare" class="btn-danger">
                ${escapeHtml(t("share.danger.stop"))}
              </button>
            </div>
          </div>
          `
              : ""
          }
        </div>
      `,
      buttons: [
        { text: t("common.done"), action: "close", className: "modal-btn-primary" },
      ],
    });

    let resolved = false;

    const cleanup = () => {
      closeModal(modalId);
      modal.remove();
    };

    const handleClose = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(null);
    };

    // Event handlers
    modal
      .querySelector('[data-action="close"]')
      .addEventListener("click", handleClose);
    modal.querySelector(".modal-close")?.addEventListener("click", handleClose);
    modal
      .querySelector(".modal-backdrop")
      ?.addEventListener("click", handleClose);

    // Invite handler
    if (isAdmin) {
      modal
        .querySelector("#btn-invite")
        ?.addEventListener("click", async () => {
          const username = modal.querySelector("#invite-username").value.trim();
          const role = modal.querySelector("#invite-role").value;
          const errorEl = modal.querySelector("#invite-error");

          if (!username) {
            showError(errorEl, t("share.error.noUsername"));
            return;
          }
          const currentUser = engine.account.user();
          if (
            currentUser &&
            username.toLowerCase() === currentUser.username.toLowerCase()
          ) {
            showError(errorEl, t("share.error.inviteSelf"));
            return;
          }

          try {
            await inviteUser(shareId, username, role);
            // Refresh modal
            cleanup();
            const result = await showManageShareModal(shareId, docId);
            resolve(result);
          } catch (error) {
            showError(errorEl, error.message);
          }
        });
    }

    const rerenderModal = async () => {
      cleanup();
      resolve(await showManageShareModal(shareId, docId));
    };
    bindRestoreButtons(modal, docId || shareId, rerenderModal);
    bindAuthorityNotice(modal, shareId, rerenderModal);
    refreshAuthorityNotice(modal, shareId, isOwner, rerenderModal);

    // Role change handlers
    modal.querySelectorAll(".permission-role").forEach((select) => {
      select.addEventListener("change", async (e) => {
        const userId = e.target.closest(".permission-item").dataset.userId;
        const newRole = e.target.value;
        try {
          await updateUserRole(shareId, userId, newRole);
        } catch (error) {
          console.error("[Share] Error updating role:", error);
          e.target.value = e.target.dataset.originalRole || "viewer";
        }
      });
    });

    // Remove user handlers
    modal.querySelectorAll(".btn-remove-user").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.stopPropagation();
        const row = btn.closest(".permission-item");
        if (!row) return;
        const userId = row.dataset.userId;
        const username =
          row.querySelector(".permission-username")?.textContent || t("share.member.thisUser");

        if (confirm(t("share.member.removeConfirm", { name: username }))) {
          try {
            await removeUser(shareId, userId);
            row.remove();

            // Use server count to avoid stale DOM (e.g. concurrent invite acceptance)
            let remainingUsers = 0;
            try {
              const permResp = await fetch(`/api/shares/${shareId}/permissions`, {
                credentials: "include",
              });
              if (permResp.ok) {
                const perms = await permResp.json();
                remainingUsers = perms.filter(p => p.role !== "owner").length;
              } else {
                remainingUsers = modal.querySelectorAll(".permission-item").length;
              }
            } catch {
              remainingUsers = modal.querySelectorAll(".permission-item").length;
            }

            const countEl = modal.querySelector(".permission-count");
            if (countEl) {
              countEl.textContent = memberCountText(remainingUsers);
            }

            // If no users remain, share was auto-stopped
            if (remainingUsers === 0) {
              console.log("[Share] No users remaining - share auto-stopped");
              resolved = true;
              cleanup();
              resolve({ action: "unshared", shareId });
            }
          } catch (error) {
            // The progress card shows the reason (or "Cancelled").
            if (!isCancelled(error)) console.error("[Share] Error removing user:", error);
          }
        }
      });
    });

    // Cancel invitation handlers
    modal.querySelectorAll(".btn-cancel-invite").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.stopPropagation();
        const row = btn.closest(".pending-invitation");
        if (!row) return;

        const invitationId = row.dataset.invitationId;
        const username =
          row.querySelector(".permission-username")?.textContent || t("share.member.thisUser");

        if (confirm(t("share.pending.cancelConfirm", { name: username }))) {
          try {
            await cancelInvitation(shareId, invitationId);
            row.remove();

            const remainingPending = modal.querySelectorAll(
              ".pending-invitation",
            ).length;
            const pendingSection = modal.querySelector(
              ".pending-invitations-section",
            );
            if (remainingPending === 0 && pendingSection) {
              pendingSection.remove();
            } else {
              const countEl =
                pendingSection?.querySelector(".permission-count");
              if (countEl) {
                countEl.textContent = t("share.pending.count", { count: remainingPending });
              }
            }
          } catch (error) {
            console.error("[Share] Error canceling invitation:", error);
            alert(t("share.pending.cancelFailed"));
          }
        }
      });
    });

    // Unshare handler
    if (isOwner) {
      modal
        .querySelector("#btn-unshare")
        ?.addEventListener("click", async () => {
          if (
            confirm(
              t("share.danger.stopConfirm"),
            )
          ) {
            try {
              await unshare(shareId);
              resolved = true;
              cleanup();
              resolve({ action: "unshared", shareId });
            } catch (error) {
              // The progress card shows the reason (or "Cancelled").
              if (!isCancelled(error)) console.error("[Share] Error unsharing:", error);
            }
          }
        });
    }

    document.body.appendChild(modal);
    showModal(modalId);
    } catch (err) {
      console.error("[Share] Unexpected error in manage modal:", err);
      resolve(null);
    }
    })();
  });
}

// ============================================
// API Functions
// ============================================

/**
 * Share a document with a user (unified model).
 * Uploads the owner's wrapped DEK to the server and sends an invitation.
 */
async function createShare({ docId, title, username, role }) {
  if (!keypairManager) {
    throw new Error(t("share.error.notReady"));
  }

  // Reconcile against the server's authoritative wrap before reading
  // the local DEK. Defensive: createShare is mostly used on the first
  // share (where local wrap = server wrap by construction), but if a
  // share→revoke→re-share cycle ever lands here with a stale local
  // wrap, this prevents propagating the stale key to the recipient.
  await VaultStorage.reconcileWrappedDEKWithServer(docId);

  // Get the existing DEK for this document from the vault
  const dekBytes = await VaultStorage.getDocDEK(docId);

  // Ensure the owner's wrapped DEK is on the server
  const ownPubKey = keypairManager.keypair.publicKey;
  const ownerWrappedBytes = await Keypair.wrapDEKAnonymous(dekBytes, ownPubKey);
  const ownerWrappedB64 = Keypair.toBase64(ownerWrappedBytes);

  const dekUploadResp = await fetch(`/api/docs/${docId}/keys/me`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({ wrapped_dek: ownerWrappedB64, only_if_absent: true }),
  });
  if (!dekUploadResp.ok) {
    throw new Error(t("share.error.ownerKeyFailed"));
  }

  dekBytes.fill(0);
  await sendInvitation(docId, username, role, title);
  return { id: docId };
}

/**
 * Authorize and send one invitation. The map title travels encrypted under
 * the map key, for the invitee only (S-M1). If the invitee's key differs
 * from the one this vault pinned for them, the user must verify the new
 * fingerprint first (A2).
 */
async function sendInvitation(docId, username, role, title) {
  let authorization;
  try {
    authorization = await MapKeyTrust.prepareInvite(docId, username, role, { title });
  } catch (error) {
    if (error?.code !== "identity_changed") throw error;
    const { confirmChangedIdentity } = await import("./identity-repair.js");
    const confirmed = await confirmChangedIdentity(error.identity, {
      confirmText: t("share.invite.verifiedInvite"),
      context: t("share.invite.keyContext", { name: error.identity.username || t("account.keyTrust.thisUser") }),
    });
    if (!confirmed) throw new Error(t("share.invite.keyNotVerified"));
    authorization = await MapKeyTrust.prepareInvite(docId, username, role, { title, confirmedKey: error.identity.publicKey });
  }

  const response = await fetch(`/api/shares/${docId}/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    credentials: "include",
    body: JSON.stringify({
      recipient: username,
      role,
      wrapped_dek: authorization.wrappedDEK,
      key_authority: authorization.event,
      ...(authorization.encryptedTitle && { encrypted_title: authorization.encryptedTitle }),
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const refusal = INVITE_REFUSALS[error.code];
    const failure = new Error(refusal ? t(refusal, { name: username }) : (error.error || t("share.error.inviteFailed")));
    failure.code = error.code;
    throw failure;
  }
  authorization.commit();
  return response.json().catch(() => ({}));
}

/**
 * Fetch share details including permissions and pending invitations.
 * Exported: the mobile doc-properties sheet uses it to display sharing
 * state (members, roles, pending invitations).
 */
/**
 * Badge (and, for the owner, a Restore button) for a member or pending
 * invitee who re-registered their encryption key: the map key they hold is
 * sealed to the replaced key until the owner restores their access.
 */
function keyRestoreHtml(needsRestore, userId, username, isOwner) {
  if (!needsRestore) return "";
  const title = username ? t("share.key.reregistered", { name: username }) : t("share.key.reregisteredUnknown");
  return `<span class="permission-key-changed" title="${escapeAttr(title)}">${escapeHtml(t("share.key.changed"))}</span>` +
    (isOwner ? `<button class="btn-restore-key" data-restore-user-id="${escapeAttr(userId)}" data-restore-username="${escapeAttr(username || "")}" title="${escapeAttr(t("share.key.restoreTitle"))}">${escapeHtml(t("share.key.restore"))}</button>` : "");
}

function bindRestoreButtons(root, docId, rerender) {
  root.querySelectorAll(".btn-restore-key").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const userId = btn.dataset.restoreUserId;
      const username = btn.dataset.restoreUsername;
      btn.disabled = true;
      btn.textContent = t("share.key.restoring");
      try {
        // A2: shows both fingerprints; the owner must confirm they compared
        // the new one with the member out of band.
        const { confirmAndRestoreMemberAccess } = await import("./identity-repair.js");
        if (await confirmAndRestoreMemberAccess(docId, userId, username)) {
          await rerender();
          return;
        }
      } catch (err) {
        console.error("[Share] Restore access failed:", err);
        alert(t("share.key.restoreFailed", { name: username, error: err?.message || err }));
      }
      btn.disabled = false;
      btn.textContent = t("share.key.restore");
    });
  });
  bindPinWarnings(root, docId, rerender);
}

/**
 * Members whose key in this map's verified state differs from the key the
 * vault pinned for them (A2: e.g. after an owner-signed rekey, which the
 * other members' clients accept). Computed locally, not asserted by the server.
 * @returns {Map<string, Object>} userId -> warning
 */
function pinWarningsFor(docId) {
  try {
    return new Map(MapKeyTrust.getPinWarnings(docId).map((w) => [w.userId, w]));
  } catch (err) {
    console.warn("[Share] Pin check failed:", err);
    return new Map();
  }
}

function pinWarningHtml(warning) {
  if (!warning) return "";
  const text = warning.previousKey ? t("share.key.old") : t("share.key.differs");
  const title = warning.previousKey
    ? t("share.key.oldTitle")
    : t("share.key.differsTitle");
  return `<span class="permission-key-changed" title="${escapeAttr(title)}">${escapeHtml(text)}</span>` +
    (warning.previousKey ? "" : `<button class="btn-verify-key" data-verify-user-id="${escapeAttr(warning.userId)}" title="${escapeAttr(t("share.key.verifyTitle"))}">${escapeHtml(t("share.key.verify"))}</button>`);
}

function bindPinWarnings(root, docId, rerender) {
  root.querySelectorAll(".btn-verify-key").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const warning = pinWarningsFor(docId).get(btn.dataset.verifyUserId);
      if (!warning) return;
      const { confirmChangedIdentity } = await import("./identity-repair.js");
      if (await confirmChangedIdentity(warning, { context: t("share.key.differsContext") })) await rerender();
    });
  });
}

/**
 * S-H1: sharing changes of this map that couldn't be verified and are
 * ignored. The owner re-confirms the map's sharing (a superseding event).
 */
function authorityNoticeHtml(docId, isOwner) {
  const skipped = MapKeyTrust.getUnverifiedEvents(docId);
  if (!skipped.length) return "";
  return `<div class="share-error" style="display:block" data-ref="authority-notice">
    ${escapeHtml(t("share.authority.skipped", { count: skipped.length }))}
    ${isOwner ? `<button class="btn-invite" data-ref="btn-reconfirm">${escapeHtml(t("share.authority.reconfirm"))}</button>`
      : escapeHtml(t("share.authority.ownerMustReconfirm"))}
  </div>`;
}

/**
 * Walk the map's authority history in the background (without pausing sync
 * on failure) and show the notice if unverifiable changes turn up.
 */
function refreshAuthorityNotice(root, docId, isOwner, rerender) {
  if (!VaultStorage.getDoc(docId)?.keyAuthority) return;
  const warned = () => [...pinWarningsFor(docId).keys()].sort().join();
  const before = warned();
  MapKeyTrust.inspectAuthority(docId).then((skipped) => {
    if (!root.isConnected) return;
    // A newer verified state (e.g. an owner's rekey of a member) changed
    // which keys disagree with the pins: show it.
    if (warned() !== before) return rerender();
    const slot = root.querySelector('[data-ref="authority-slot"]');
    if (!slot || !skipped.length || slot.childElementCount) return;
    slot.innerHTML = authorityNoticeHtml(docId, isOwner);
    bindAuthorityNotice(slot, docId, rerender);
  }).catch(() => {});
}

function bindAuthorityNotice(root, docId, rerender) {
  root.querySelector('[data-ref="btn-reconfirm"]')?.addEventListener("click", async (e) => {
    e.target.disabled = true;
    try {
      await MapKeyTrust.ensure(docId);
      await rerender();
    } catch (err) {
      console.error("[Share] Re-confirming sharing failed:", err);
      alert(err?.message || t("share.authority.reconfirmFailed"));
      e.target.disabled = false;
    }
  });
}

export async function fetchShareDetails(shareId) {
  const response = await fetch(`/api/shares/${shareId}`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to load share details");
  }

  const share = await response.json();

  // Try to load permissions
  try {
    const permResponse = await fetch(`/api/shares/${shareId}/permissions`, {
      credentials: "include",
    });
    if (permResponse.ok) {
      share.permissions = await permResponse.json();
    }
  } catch (e) {
    share.permissions = [];
  }

  // Try to load pending invitations
  try {
    const invResponse = await fetch(`/api/shares/${shareId}/invitations`, {
      credentials: "include",
    });
    if (invResponse.ok) {
      share.pendingInvitations = await invResponse.json();
    }
  } catch (e) {
    share.pendingInvitations = [];
  }

  return share;
}

/**
 * Invite a user to a share
 */
/** The server's refusals against invitation spam, by code (invitations.rs). */
const INVITE_REFUSALS = {
  invitation_waiting: "share.error.invitationWaiting",
  invitations_sent_per_day: "share.error.sentPerDay",
  invitations_received_per_day: "share.error.receivedPerDay",
};

export async function inviteUser(shareId, username, role) {
  if (!keypairManager) {
    throw new Error(t("share.error.notReady"));
  }

  // Reconcile against the server's authoritative wrapped DEK before
  // wrapping for the recipient. If the local vault is stale (e.g., a
  // previous DEK rotation completed server-side but our local
  // updateDocWrappedDEK never ran, or the rotation happened on another
  // device and we haven't reopened this doc since), we'd otherwise wrap
  // an out-of-date DEK and hand the recipient a key that can't decrypt
  // the server's baseline. 404 here is fine — it means the doc has no
  // doc_keys rows yet (we'll create the owner row a few lines down) so
  // there's nothing to reconcile against.
  await VaultStorage.reconcileWrappedDEKWithServer(shareId);

  // Get DEK from unified vault (works for both personal docs and shares)
  const dek = await VaultStorage.getDocDEK(shareId);

  // Ensure the owner's wrapped DEK is on the server. This is idempotent:
  // PUT /docs/:id/keys/me UPDATEs an existing row or INSERTs an owner
  // row if there are no rows for this doc yet. Without this step,
  // inviting via the manage-share modal on a doc that was never run
  // through createShare leaves the owner WITHOUT a doc_keys row —
  // breaking every later op that looks up the owner's wrap (rotation,
  // /my-dek, the reconcile we just did, etc.). Same payload + endpoint
  // as createShare, deliberately.
  const ownPubKey = keypairManager.keypair.publicKey;
  const ownerWrappedBytes = await Keypair.wrapDEKAnonymous(dek, ownPubKey);
  const ownerWrappedB64 = Keypair.toBase64(ownerWrappedBytes);
  const dekUploadResp = await fetch(`/api/docs/${shareId}/keys/me`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({ wrapped_dek: ownerWrappedB64, only_if_absent: true }),
  });
  if (!dekUploadResp.ok) {
    throw new Error(t("share.error.ownerKeyFailed"));
  }

  dek.fill(0);

  // The vault title, encrypted for the invitee only.
  const localDoc = VaultStorage.getIndex()?.docs?.find((d) => d.docId === shareId);
  return sendInvitation(shareId, username, role, localDoc?.title || "");
}

/**
 * Cancel a pending invitation
 */
export async function cancelInvitation(shareId, invitationId) {
  const authority = await MapKeyTrust.ensure(shareId);
  const invitationsResponse = await fetch(`/api/shares/${shareId}/invitations`, { credentials: 'include' });
  if (!invitationsResponse.ok) throw new Error(t('share.error.invitationsUnavailable'));
  const invitation = (await invitationsResponse.json()).find(i => i.id === invitationId);
  if (!invitation) throw new Error(t('share.error.invitationGone'));
  const member = authority.members.find(m => m.userId === invitation.invitee_id && m.pending);
  if (!member) throw new Error(t('share.error.invitationMismatch'));
  const members = authority.members.filter(m => m.userId !== member.userId);
  const dek = await VaultStorage.getDocDEK(shareId);
  let key_authority;
  try { key_authority = await MapKeyTrust.prepare(shareId, 'cancel', members, dek, authority); }
  finally { dek.fill(0); }
  const response = await fetch(
    `/api/shares/${shareId}/invitations/${invitationId}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ key_authority }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || t("share.error.cancelInviteFailed"));
  }
}

/**
 * Update a user's role
 */
export async function updateUserRole(shareId, userId, newRole) {
  const authority = await MapKeyTrust.ensure(shareId);
  const members = authority.members.map(m => m.userId === userId ? { ...m, role: newRole } : m);
  const dek = await VaultStorage.getDocDEK(shareId);
  let key_authority;
  try { key_authority = await MapKeyTrust.prepare(shareId, 'role', members, dek, authority); }
  finally { dek.fill(0); }
  const response = await fetch(`/api/shares/${shareId}/permissions/${userId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    credentials: "include",
    body: JSON.stringify({ role: newRole, key_authority }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || t("share.error.roleFailed"));
  }
}

/**
 * Run a key rotation (removing members) with a progress card and a Cancel
 * button: it re-encrypts and uploads the map content. Cancelling is offered
 * until the upload is complete and changes nothing. A cancel rejects with an
 * error whose `code` is "cancelled"; callers show no error for it.
 * @param {string} title
 * @param {(ctx: {report: Function, signal: AbortSignal, setCancellable: Function}) => Promise<void>} run
 */
async function withRotationProgress(title, run) {
  const controller = new AbortController();
  const progress = beginOperation(title, { onCancel: () => controller.abort() });
  try {
    await progress.paint();
    await run({ report: progress.update, signal: controller.signal, setCancellable: progress.setCancellable });
    progress.finish(t("common.done"));
  } catch (error) {
    if (error?.name === "AbortError") {
      progress.cancelled(t("share.rotation.cancelled"));
      throw Object.assign(new Error("Cancelled"), { code: "cancelled" });
    }
    progress.fail(error);
    throw error;
  }
}

/** True for the error a cancelled removal rejects with (not a failure). */
export function isCancelled(error) {
  return error?.code === "cancelled";
}

/**
 * Remove a user from a share via atomic rotate+revoke.
 * The server creates the revocation snapshot internally from the current
 * encrypted baseline — no client-side snapshot needed.
 */
export async function removeUser(shareId, userId) {
  console.log("[Share] Removing user via atomic rotate+revoke:", { shareId, userId });
  await withRotationProgress(t("share.rotation.removing"), (ctx) =>
    rotateShareDEK(shareId, { revokeUserIds: [userId], ...ctx }));
  console.log("[Share] User removed (atomic rotate+revoke complete)");
}

/**
 * Stop sharing — revokes all non-owner users via atomic rotate+revoke.
 * The server creates the revocation snapshot internally.
 * @param {string} shareId - The share ID
 */
export async function unshare(shareId) {
  await withRotationProgress(t("share.rotation.stopping"), async (ctx) => {
    ctx.report({ label: t("share.rotation.checking") });
    // Fetch current permissions to get all non-owner user IDs
    const permsResp = await fetch(`/api/shares/${shareId}/permissions`, { credentials: "include" });
    if (!permsResp.ok) throw new Error(t("share.rotation.permissionsFailed"));
    const permissions = await permsResp.json();
    const nonOwnerIds = permissions
      .filter(p => p.role !== "owner")
      .map(p => p.user_id);

    // The map's link goes too: sharing stops for everyone.
    const link = await engine.sharing.link.get(shareId);
    if (nonOwnerIds.length === 0 && !link) {
      console.log("[Share] No non-owner users to revoke");
      return;
    }

    console.log("[Share] Stopping sharing via atomic rotate+revoke:", { shareId, revoking: nonOwnerIds.length });
    await rotateShareDEK(shareId, { revokeUserIds: nonOwnerIds, turnOffLink: true, ...ctx });
    console.log("[Share] Sharing stopped (atomic rotate+revoke complete)");
  });
}

// ============================================
// Utility Functions
// ============================================

function showError(element, message) {
  element.textContent = message;
  element.style.display = "block";
}

// escapeHtml / escapeAttr come from engine/core/validation.js (imported at top) —
// the previous local DOM-based escapeHtml did not escape quotes.

// ============================================
// Inline sharing section (for doc-properties-modal)
// ============================================

/**
 * Render sharing controls inline into a container element.
 * Replaces the "Share..." button with the actual sharing UI embedded directly
 * in the doc-properties modal — no second modal needed.
 *
 * @param {HTMLElement} container - DOM element to render into
 * @param {string} docId - The document ID
 * @param {Object} [opts]
 * @param {Function} [opts.onClose] - Called when the section triggers a close (e.g. stop sharing)
 */
/**
 * Clean up event listeners for the sharing section.
 * Call this when the containing modal is closed.
 */
export function cleanupSharingSection() {
  if (_shareSectionUnsub) {
    _shareSectionUnsub();
    _shareSectionUnsub = null;
  }
}

export async function renderSharingSection(container, docId, { onClose } = {}) {
  // A map on this device can't be shared: say why, and offer to move it online.
  if (mapStorageOf(docId) === "local") {
    container.innerHTML = `
      <div class="sharing-local">
        <p>${escapeHtml(t(isOnlineSlotEnabled() ? "share.local.message" : "share.local.noServer"))}</p>
        ${isOnlineSlotEnabled() ? `<button type="button" class="btn-invite" data-ref="move-online">${escapeHtml(t("vault.map.moveOnline"))}</button>` : ""}
      </div>`;
    container.querySelector('[data-ref="move-online"]')?.addEventListener("click", async () => {
      if (await moveMapOnline(docId)) await renderSharingSection(container, docId, { onClose });
    });
    return;
  }
  // Open by its link, not as a member: what that means, the link to share
  // further, and removing it from this account's maps.
  const held = engine.sharing.link.held(docId);
  if (held) {
    renderLinkHolderSection(container, docId, held, { onClose });
    return;
  }
  container.innerHTML = `<div class="sharing-section-loading">${escapeHtml(t("share.section.loading"))}</div>`;
  try {
    // In the unified model, docId IS the share id — no separate shareId.
    // Always show the manage section — it handles both shared and unshared docs:
    // - For owners: shows invite form + permissions list (or "no users" if unshared)
    // - For recipients: shows permissions list (read-only or editable depending on role)
    await _renderManageSection(container, docId, docId, { onClose });
  } catch (e) {
    container.innerHTML = `<div class="sharing-section-error">${escapeHtml(t("share.section.loadFailed"))}</div>`;
    console.error("[Share] renderSharingSection error:", e);
  }
}

// Track event listener for live invitation updates (cleaned up on re-render)
let _shareSectionUnsub = null;

async function _renderManageSection(container, shareId, docId, { onClose } = {}) {
  // Clean up previous event listener if re-rendering
  if (_shareSectionUnsub) {
    _shareSectionUnsub();
    _shareSectionUnsub = null;
  }

  container.innerHTML = `<div class="sharing-section-loading">${escapeHtml(t("share.section.loadingDetails"))}</div>`;

  let share;
  try {
    share = await fetchShareDetails(shareId);
  } catch (e) {
    container.innerHTML = `<div class="sharing-section-error">${escapeHtml(t("share.section.detailsFailed"))}</div>`;
    return;
  }

  // Server returns empty title (E2EE) — use local vault title instead
  const vaultIndex = VaultStorage.getIndex();
  const localDoc = vaultIndex?.docs?.find((d) => d.docId === shareId);
  if (localDoc?.title) share.title = localDoc.title;
  if (!share.title) share.title = t("common.untitled");

  const isOwner = share.role === "owner";
  const isAdmin = share.role === "admin" || isOwner;
  // The map's link (docs/plans/2026-10-28-link-sharing.md): null when none.
  const link = await engine.sharing.link.get(shareId).catch(() => null);
  const isEditor = share.role === "editor";
  const canViewPermissions = true; // Every member can inspect the shared document roster.

  // Exclude owner from permissions list — shown separately in header
  const nonOwnerPerms = (share.permissions || []).filter(p => p.role !== "owner");
  const pinWarnings = pinWarningsFor(shareId);
  let permissionsHtml = "";
  if (nonOwnerPerms.length > 0) {
    permissionsHtml = nonOwnerPerms
      .map((p) => {
        let actionsHtml = "";
        const removeBtn = `
          <button class="btn-remove-user" title="${escapeAttr(t("share.member.remove"))}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        `;
        if (isAdmin) {
          // Removal is owner-only on the backend (revoke + DEK rotation) —
          // see the matching guard in the other render path above.
          actionsHtml = `
            <select class="permission-role" data-original-role="${escapeAttr(p.role)}">
              ${roleOptions(["viewer", "editor", "admin"], p.role)}
            </select>
            ${isOwner ? removeBtn : ""}
          `;
        } else if (isEditor) {
          if (p.role === "viewer" || p.role === "editor") {
            actionsHtml = `
              <select class="permission-role" data-original-role="${escapeAttr(p.role)}">
                ${roleOptions(["viewer", "editor"], p.role)}
              </select>
            `;
          } else {
            actionsHtml = roleLabelHtml(p.role);
          }
        } else {
          actionsHtml = roleLabelHtml(p.role);
        }
        return `
          <div class="permission-item" data-user-id="${escapeAttr(p.user_id)}" data-role="${escapeAttr(p.role)}">
            <div class="permission-user">
              <span class="permission-avatar">${escapeHtml((p.username || "?")[0].toUpperCase())}</span>
              <span class="permission-username">${escapeHtml(p.username || t("share.member.unknown"))}</span>
            </div>
            <div class="permission-actions">${keyRestoreHtml(p.key_restore_required, p.user_id, p.username, isOwner)}${pinWarningHtml(pinWarnings.get(p.user_id))}${actionsHtml}</div>
          </div>
        `;
      })
      .join("");
  } else {
    permissionsHtml = `
      <div class="empty-permissions">
        <p>${escapeHtml(t("share.members.empty"))}</p>
        <span>${escapeHtml(t("share.members.emptyHint"))}</span>
      </div>
    `;
  }

  container.innerHTML = `
    <div class="manage-share-content">
      <div class="share-status-header">
        <div class="share-status-icon ${isOwner ? "owner" : ""}">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        </div>
        <div class="share-status-details">
          <h3 class="share-title">${escapeHtml(share.title)}</h3>
          <div class="share-meta">
            <span class="share-role-badge ${escapeAttr(share.role)}">${escapeHtml(roleInText(share.role))}</span>
            ${share.owner_username && !isOwner ? `<span class="share-owner">${escapeHtml(t("share.manage.byOwner", { name: share.owner_username }))}</span>` : ""}
          </div>
        </div>
      </div>

      <div data-ref="authority-slot">${authorityNoticeHtml(shareId, isOwner)}</div>

      ${isAdmin ? `
      <div class="invite-section">
        <div class="section-header"><h4>${escapeHtml(t("share.invite.heading"))}</h4></div>
        <div class="invite-form">
          <div class="invite-input-group">
            <input type="text" class="form-input" placeholder="${escapeAttr(t("share.invite.username"))}" data-ref="invite-username" />
            <select class="form-select" data-ref="invite-role">
              ${roleOptions(["viewer", "editor", "admin"], "editor")}
            </select>
            <button class="btn-invite" data-ref="btn-invite">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
              ${escapeHtml(t("common.add"))}
            </button>
          </div>
          <div class="share-error" data-ref="invite-error"></div>
        </div>
      </div>
      ` : ""}

      ${linkSectionHtml(link, { isAdmin, isOwner })}

      ${canViewPermissions ? `
      <div class="permissions-section">
        <div class="section-header">
          <h4>${escapeHtml(t("share.members.heading"))}</h4>
          <span class="permission-count" data-ref="permission-count">${escapeHtml(memberCountText(nonOwnerPerms.length))}</span>
        </div>
        <div class="permissions-list">${permissionsHtml}</div>
      </div>
      ` : ""}

      ${isAdmin && share.pendingInvitations?.length > 0 ? `
      <div class="pending-invitations-section" data-ref="pending-section">
        <div class="section-header">
          <h4>${escapeHtml(t("share.pending.heading"))}</h4>
          <span class="permission-count">${escapeHtml(t("share.pending.count", { count: share.pendingInvitations.length }))}</span>
        </div>
        <div class="permissions-list">
          ${share.pendingInvitations.map((inv) => `
            <div class="permission-item pending-invitation" data-invitation-id="${escapeAttr(inv.id)}">
              <div class="permission-user">
                <span class="permission-avatar pending">${escapeHtml((inv.invitee_username || "?")[0].toUpperCase())}</span>
                <span class="permission-username">${escapeHtml(inv.invitee_username || t("share.member.unknown"))}</span>
              </div>
              <div class="permission-actions">
                ${keyRestoreHtml(inv.key_restore_required, inv.invitee_id, inv.invitee_username, isOwner)}
                ${pinWarningHtml(pinWarnings.get(inv.invitee_id))}
                <span class="permission-role-label pending">${escapeHtml(t("share.pending.role", { role: roleInText(inv.role) }))}</span>
                <button class="btn-cancel-invite" title="${escapeAttr(t("share.pending.cancel"))}">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          `).join("")}
        </div>
      </div>
      ` : ""}

      ${isOwner && (nonOwnerPerms.length > 0 || link) ? `
      <div class="danger-section">
        <div class="section-header"><h4>${escapeHtml(t("share.danger.heading"))}</h4></div>
        <div class="danger-content">
          <div class="danger-info">
            <strong>${escapeHtml(t("share.danger.stop"))}</strong>
            <p>${escapeHtml(t("share.danger.stopHint"))}</p>
          </div>
          <button class="btn-danger" data-ref="btn-unshare">${escapeHtml(t("share.danger.stop"))}</button>
        </div>
      </div>
      ` : ""}
    </div>
  `;

  const ref = (name) => container.querySelector(`[data-ref="${name}"]`);

  if (isAdmin) {
    ref("btn-invite")?.addEventListener("click", async () => {
      const username = ref("invite-username").value.trim();
      const role = ref("invite-role").value;
      const errorEl = ref("invite-error");
      if (!username) { showError(errorEl, t("share.error.noUsername")); return; }
      const currentUser = engine.account.user();
      if (currentUser && username.toLowerCase() === currentUser.username.toLowerCase()) {
        showError(errorEl, t("share.error.inviteSelf"));
        return;
      }
      try {
        await inviteUser(shareId, username, role);
        await _renderManageSection(container, shareId, docId, { onClose });
      } catch (error) {
        showError(errorEl, error.message);
      }
    });
  }

  container.querySelectorAll(".permission-role").forEach((select) => {
    select.addEventListener("change", async (e) => {
      const userId = e.target.closest(".permission-item").dataset.userId;
      const newRole = e.target.value;
      try {
        await updateUserRole(shareId, userId, newRole);
      } catch (error) {
        console.error("[Share] Error updating role:", error);
        e.target.value = e.target.dataset.originalRole || "viewer";
      }
    });
  });

  const rerenderSection = () => _renderManageSection(container, shareId, docId, { onClose });
  bindLinkSection(container, shareId, link);
  bindRestoreButtons(container, docId, rerenderSection);
  bindAuthorityNotice(container, shareId, rerenderSection);
  refreshAuthorityNotice(container, shareId, isOwner, rerenderSection);

  container.querySelectorAll(".btn-remove-user").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const row = btn.closest(".permission-item");
      if (!row) return;
      const userId = row.dataset.userId;
      const username = row.querySelector(".permission-username")?.textContent || t("share.member.thisUser");
      if (confirm(t("share.member.removeConfirm", { name: username }))) {
        try {
          await removeUser(shareId, userId);
          row.remove();
          // The link is the access: whoever has it opens the map, them too
          // if they have it. A new link cuts everyone who has the old one.
          if (link && confirm(t("share.link.stillOpenAfterRemoval", { name: username }))) {
            const address = await linkActions.renew(shareId, link.role).catch(() => null);
            if (address) await copyLink(address, null);
          }

          let remainingUsers = 0;
          try {
            const permResp = await fetch(`/api/shares/${shareId}/permissions`, { credentials: "include" });
            if (permResp.ok) {
              const perms = await permResp.json();
              remainingUsers = perms.filter(p => p.role !== "owner").length;
            } else {
              remainingUsers = container.querySelectorAll(".permission-item").length;
            }
          } catch {
            remainingUsers = container.querySelectorAll(".permission-item").length;
          }
          const countEl = ref("permission-count");
          if (countEl) countEl.textContent = memberCountText(remainingUsers);
          if (remainingUsers === 0 && !link) {
            console.log("[Share] No users remaining - share auto-stopped");
            // In unified doc model, docId === shareId. Do NOT delete — owner keeps their doc.
            if (onClose) onClose();
          }
        } catch (error) {
          // The progress card shows the reason (or "Cancelled").
          if (!isCancelled(error)) console.error("[Share] Error removing user:", error);
        }
      }
    });
  });

  container.querySelectorAll(".btn-cancel-invite").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const row = btn.closest(".pending-invitation");
      if (!row) return;
      const invitationId = row.dataset.invitationId;
      const username = row.querySelector(".permission-username")?.textContent || t("share.member.thisUser");
      if (confirm(t("share.pending.cancelConfirm", { name: username }))) {
        try {
          await cancelInvitation(shareId, invitationId);
          row.remove();
          const remainingPending = container.querySelectorAll(".pending-invitation").length;
          const pendingSection = ref("pending-section");
          if (remainingPending === 0 && pendingSection) pendingSection.remove();
        } catch (error) {
          console.error("[Share] Error canceling invitation:", error);
          alert(t("share.pending.cancelFailed"));
        }
      }
    });
  });

  if (isOwner) {
    const unshareBtn = ref("btn-unshare");
    if (unshareBtn) {
      unshareBtn.addEventListener("click", async () => {
        // Guard against double-click. Without this, two clicks land two
        // /rotate POSTs in quick succession. The first revokes the only
        // non-owner; the second races on /permissions BEFORE the first
        // commits, sees the same non-owner list, fires rotate again,
        // and the server rejects with 400 "Cannot revoke a user who is
        // not an active member" — leaving the user staring at an alert
        // even though sharing already stopped.
        if (unshareBtn.dataset.busy === "true") return;
        if (!confirm(t("share.danger.stopConfirm"))) return;
        unshareBtn.dataset.busy = "true";
        unshareBtn.disabled = true;
        try {
          await unshare(shareId);
          // In unified doc model, docId === shareId. Do NOT delete — owner keeps their doc.
          if (onClose) onClose();
        } catch (error) {
          console.error("[Share] Error unsharing:", error);
          // "not an active member" means another concurrent click already
          // unshared. Don't bother the user with a scary alert in that case.
          const msg = String(error?.message || "");
          if (/not an active member/i.test(msg)) {
            console.log("[Share] Already unshared (concurrent click) — closing modal.");
            if (onClose) onClose();
          }
          // Otherwise the progress card shows the reason (or "Cancelled").
        } finally {
          unshareBtn.dataset.busy = "false";
          unshareBtn.disabled = false;
        }
      });
    }
  }

  // Listen for invitation acceptance and re-render to update pending list;
  // the same when the map's link changes or someone joins by it.
  const offResponded = on(Events.SHARE_INVITATION_RESPONDED, ({ docId: respondedDocId }) => {
    if (respondedDocId === shareId) {
      _renderManageSection(container, shareId, docId, { onClose });
    }
  });
  const offLink = on(Events.SHARE_LINK_CHANGED, ({ docId: changedDocId }) => {
    if (changedDocId === shareId && container.isConnected) _renderManageSection(container, shareId, docId, { onClose });
  });
  _shareSectionUnsub = () => { offResponded(); offLink(); };
}

/**
 * "Anyone with the link" (docs/plans/2026-10-28-link-sharing.md, "Redesign":
 * the link is the access): the owner makes it, copies it, sets the role it
 * gives, makes a new one or turns it off; an admin copies it; other members
 * see whether it is on and as what.
 */
function linkSectionHtml(link, { isAdmin, isOwner }) {
  if (!link && !isOwner) return "";
  const heading = `<div class="section-header"><h4>${escapeHtml(t("share.link.heading"))}</h4></div>`;
  if (!isAdmin) {
    return `<div class="link-share-section">${heading}
      <p class="link-share-hint">${escapeHtml(t("share.link.status", { role: roleInText(link.role) }))}</p></div>`;
  }
  if (!link) {
    return `<div class="link-share-section" data-ref="link-section">${heading}
      <div class="link-share-row">
        <span class="link-share-state">${escapeHtml(t("share.link.off"))}</span>
        <label class="link-share-role">${escapeHtml(t("share.link.opensAs"))}
          <select class="form-select" data-ref="link-new-role">${roleOptions(["viewer", "editor"], "viewer")}</select>
        </label>
        <button class="btn-invite" data-ref="btn-link-create">${escapeHtml(t("share.link.create"))}</button>
      </div>
      <p class="link-share-hint">${escapeHtml(t("share.link.hintOff"))}</p>
      <div class="share-error" data-ref="link-error"></div>
    </div>`;
  }
  const role = isOwner
    ? `<label class="link-share-role">${escapeHtml(t("share.link.opensAs"))}
        <select class="form-select" data-ref="link-role" data-original-role="${escapeAttr(link.role)}">${roleOptions(["viewer", "editor"], link.role)}</select>
      </label>`
    : `<span class="link-share-role">${escapeHtml(t("share.link.opensAs"))} ${escapeHtml(roleLabel(link.role))}</span>`;
  return `<div class="link-share-section" data-ref="link-section">${heading}
    <div class="link-share-row">
      <span class="link-share-state on">${escapeHtml(t("share.link.on"))}</span>
      ${role}
    </div>
    <div class="link-share-address">
      <input type="text" class="form-input" readonly data-ref="link-address" placeholder="${escapeAttr(t("share.link.preparing"))}" />
      <button class="btn-invite" data-ref="btn-link-copy" disabled>${escapeHtml(t("share.link.copy"))}</button>
    </div>
    ${isOwner ? `<div class="link-share-actions">
      <button type="button" class="btn-link-action" data-ref="btn-link-renew">${escapeHtml(t("share.link.renew"))}</button>
      <button type="button" class="btn-link-action" data-ref="btn-link-off">${escapeHtml(t("share.link.turnOff"))}</button>
    </div>` : ""}
    <p class="link-share-hint">${escapeHtml(t(isOwner ? "share.link.hintOn" : "share.link.ownerOnly"))}</p>
    <div class="share-error" data-ref="link-error"></div>
  </div>`;
}

/**
 * A map open by its link (not a member): the role the link gives, the link
 * to share further (anyone with it opens the map), and "Remove from my
 * maps" (kept ones; nothing changes on the server). When the link no longer
 * works, say so.
 */
function renderLinkHolderSection(container, docId, held, { onClose } = {}) {
  container.innerHTML = `
    <div class="link-holder-section">
      <div class="section-header"><h4>${escapeHtml(t(held.gone ? "share.link.gone.title" : "share.link.holder.title"))}</h4></div>
      <p class="link-share-hint">${escapeHtml(held.gone ? t(held.kept ? "share.link.gone.text" : "share.link.gone.textPage") : t("share.link.holder.text", { role: roleInText(held.role) }))}</p>
      ${held.gone ? "" : `<div class="link-share-address">
        <input type="text" class="form-input" readonly data-ref="link-address" value="${escapeAttr(held.address)}" />
        <button class="btn-invite" data-ref="btn-link-copy">${escapeHtml(t("share.link.copy"))}</button>
      </div>`}
      ${held.kept ? `<div class="link-share-actions">
        <button type="button" class="btn-link-action danger" data-ref="btn-link-remove">${escapeHtml(t("share.link.holder.remove"))}</button>
      </div>` : ""}
    </div>`;
  const field = container.querySelector('[data-ref="link-address"]');
  container.querySelector('[data-ref="btn-link-copy"]')?.addEventListener("click", () => copyLink(field.value, field));
  field?.addEventListener("focus", () => field.select());
  // The vault's own removal: asked, removed, another map opened when it was
  // the one open.
  container.querySelector('[data-ref="btn-link-remove"]')?.addEventListener("click", async () => {
    const { confirmLeaveShare } = await import("./vault/actions.js");
    await confirmLeaveShare(docId);
    if (!engine.maps.get(docId)) onClose?.();
  });
}

/** Copy a link: the clipboard, else the address field selected for the person to copy. */
async function copyLink(address, field) {
  try {
    await navigator.clipboard.writeText(address);
    showToast(t("share.link.copied"));
  } catch {
    field?.focus();
    field?.select();
    showToast(t("share.link.copyFailed"));
  }
}

/**
 * The map's link, for the computer's panel and the phone's sheet alike: the
 * engine's link functions (docs/plans/2026-10-28-link-sharing.md), the three
 * that change the map key with their progress card, and copying a link.
 */
export const linkActions = {
  get: (docId) => engine.sharing.link.get(docId),
  address: (docId) => engine.sharing.link.address(docId),
  create: (docId, role) => engine.sharing.link.create(docId, role),
  setRole: (docId, role) => engine.sharing.link.setRole(docId, role),
  turnOff: (docId) => withRotationProgress(t("share.link.turningOff"), (ctx) => engine.sharing.link.turnOff(docId, ctx)),
  async renew(docId, role) {
    let address = null;
    await withRotationProgress(t("share.link.renewing"), async (ctx) => {
      address = await engine.sharing.link.renew(docId, role, ctx);
    });
    return address;
  },
  copy: (address, field = null) => copyLink(address, field),
  // A map open by its link (not a member): { role, gone, kept, address }, or null.
  held: (docId) => engine.sharing.link.held(docId),
};

function bindLinkSection(container, shareId, link) {
  const ref = (name) => container.querySelector(`[data-ref="${name}"]`);
  const errorEl = ref("link-error");
  if (!errorEl) return;
  const busy = (button, run) => async () => {
    if (button.dataset.busy === "true") return;
    button.dataset.busy = "true";
    button.disabled = true;
    errorEl.style.display = "none";
    try {
      await run();
    } catch (error) {
      if (!isCancelled(error)) showError(errorEl, error.message);
    } finally {
      button.dataset.busy = "false";
      button.disabled = false;
    }
  };
  const create = ref("btn-link-create");
  if (create) {
    create.addEventListener("click", busy(create, async () => {
      await copyLink(await linkActions.create(shareId, ref("link-new-role").value), null);
    }));
    return;
  }
  const field = ref("link-address");
  const copy = ref("btn-link-copy");
  linkActions.address(shareId).then((address) => {
    if (!address || !field.isConnected) return;
    field.value = address;
    copy.disabled = false;
  }).catch((error) => showError(errorEl, error.message));
  copy.addEventListener("click", () => copyLink(field.value, field));
  field.addEventListener("focus", () => field.select());
  const roleSelect = ref("link-role");
  // Only the owner changes it (an admin copies it).
  roleSelect?.addEventListener("change", async () => {
    try {
      await linkActions.setRole(shareId, roleSelect.value);
    } catch (error) {
      roleSelect.value = roleSelect.dataset.originalRole;
      showError(errorEl, error.message);
    }
  });
  const renew = ref("btn-link-renew");
  renew?.addEventListener("click", busy(renew, async () => {
    if (!confirm(t("share.link.renewConfirm"))) return;
    const address = await linkActions.renew(shareId, link.role);
    if (address) await copyLink(address, null);
  }));
  const off = ref("btn-link-off");
  off?.addEventListener("click", busy(off, async () => {
    if (!confirm(t("share.link.turnOffConfirm"))) return;
    await linkActions.turnOff(shareId);
  }));
}

function _renderCreateSection(container, docId, { onClose } = {}) {
  const vaultIndex = VaultStorage.getIndex();
  const docEntry = vaultIndex?.docs?.find((m) => m.docId === docId);
  const docTitle = docEntry?.title || t("common.untitled");

  container.innerHTML = `
    <div class="share-form">
      <div class="form-group">
        <label>${escapeHtml(t("share.invite.heading"))}</label>
        <div class="input-with-icon">
          <input type="text" class="form-input" data-ref="share-username" placeholder="${escapeAttr(t("share.invite.username"))}" autocomplete="off" />
          <span class="input-icon" data-ref="username-status"></span>
        </div>
        <span class="form-hint" data-ref="username-hint"></span>
      </div>
      <div class="form-group">
        <label>${escapeHtml(t("share.invite.permission"))}</label>
        <select class="form-select" data-ref="share-role">
          ${roleOptions(["viewer", "editor", "admin"], "editor")}
        </select>
      </div>
      <div class="permission-legend">
        ${roleLegendHtml()}
      </div>
      <div class="share-error" data-ref="share-error"></div>
      <div class="share-form-actions">
        <button class="modal-btn-primary" data-ref="share-btn">${escapeHtml(t("common.share"))}</button>
      </div>
    </div>
  `;

  const ref = (name) => container.querySelector(`[data-ref="${name}"]`);

  let usernameTimeout = null;
  ref("share-username").addEventListener("input", (e) => {
    const hint = ref("username-hint");
    const username = e.target.value.trim();
    if (usernameTimeout) clearTimeout(usernameTimeout);
    hint.textContent = "";
    hint.className = "form-hint";
    if (username.length >= 2) {
      usernameTimeout = setTimeout(async () => {
        try {
          await keypairManager.getUserPublicKey(username);
          hint.textContent = t("share.invite.userFound");
          hint.className = "form-hint hint-success";
        } catch {
          hint.textContent = t("share.invite.userNotFound");
          hint.className = "form-hint hint-error";
        }
      }, 500);
    }
  });

  const handleShare = async () => {
    const username = ref("share-username").value.trim();
    const role = ref("share-role").value;
    const errorEl = ref("share-error");
    const shareBtn = ref("share-btn");

    if (!username) { showError(errorEl, t("share.error.noUsername")); return; }
    const currentUser = engine.account.user();
    if (currentUser && username.toLowerCase() === currentUser.username.toLowerCase()) {
      showError(errorEl, t("share.error.shareSelf"));
      return;
    }

    shareBtn.disabled = true;
    shareBtn.textContent = t("share.create.sharing");
    errorEl.style.display = "none";

    try {
      const result = await createShare({ docId, title: docTitle, username, role });
      // In the unified model, docId IS the share room — no room switch needed
      await _renderManageSection(container, result.id, docId, { onClose });
    } catch (error) {
      console.error("[Share] Error creating share:", error);
      showError(errorEl, error.message || t("share.error.createFailed"));
      shareBtn.disabled = false;
      shareBtn.textContent = t("common.share");
    }
  };

  ref("share-btn").addEventListener("click", handleShare);
  ref("share-username").addEventListener("keydown", (e) => {
    if (e.key === "Enter") handleShare();
  });

  ref("share-username").focus();
}

// ============================================
// Exports
// ============================================

/**
 * Clean up keypair manager on logout — zeros secret key material.
 */
export function destroyShareModule() {
  if (keypairManager) {
    keypairManager.destroy();
    keypairManager = null;
  }
}

export { keypairManager };
